import { type ReadonlyVec2, vec2 } from 'gl-matrix';

import { vec2extended } from '@canvas/math/vec2';

export { findClosestAnchorWithinRadius } from './anchor.ts';

import { walkAndConvertPathSegmentsToLines } from './path-traversal.ts';
import { createLine } from './segments/line.ts';
import {
  type PathSegment,
  type PathSegmentLine,
  type Line,
  type PathSegmentArc,
  type PathProportion,
  type LineWithUV,
  type PathHandleMetadata,
  type PathSegmentCubicBezier,
  type PathSegmentMetadata,
} from './types.ts';

export type {
  PathSegment,
  PathSegmentLine,
  PathSegmentArc,
  PathSegmentCubicBezier,
  Line,
  LineWithUV,
  PathProportion,
  PathHandleMetadata,
  PathSegmentMetadata,
};

/**
 * Computes the segments joining two points.
 */
export const getPathSegmentsFromPoints = (
  from: ReadonlyVec2,
  to: ReadonlyVec2,
  pathSegmentMetadataArray?: PathSegmentMetadata[],
): PathSegmentLine[] => {
  const angle = vec2extended.angleFrom(from, to);
  const length = vec2.dist(from, to);
  return [createLine(angle, length, pathSegmentMetadataArray?.[0])];
};

/**
 * Computes the segments joining many points.
 */
export const getPathSegmentsFromPointsArray = (
  points: vec2[],
  pathSegmentMetadataArray?: PathSegmentMetadata[],
): PathSegmentLine[] => {
  const segments: PathSegmentLine[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const from = points[i];
    const to = points[i + 1];
    const angle = vec2extended.angleFrom(from, to);
    const length = vec2.dist(from, to);
    segments.push(createLine(angle, length, pathSegmentMetadataArray?.[i]));
  }
  return segments;
};

/**
 * Turns a start point and an array of segments into an array of lines
 */
export const pathSegmentsToLines = (
  start: ReadonlyVec2,
  segments: PathSegment[],
  numLines = 5,
  lines: LineWithUV[] = [],
): Line[] => {
  walkAndConvertPathSegmentsToLines(
    start,
    segments,
    (start, end, uv) => {
      lines.push({ start, end, uv });
    },
    {
      numLines,
      overlap: true,
    },
  );

  return lines;
};

export * from './segments/arc.ts';
export * from './segments/cap.ts';
export * from './segments/line.ts';
export * from './segments/cubic-bezier.ts';
export * from './segments/segments.ts';
export * from './path-bbox.ts';
export * from './path-intersection.ts';
export * from './path-points.ts';
export * from './path-modify.ts';
export * from './path-traversal.ts';
export * from './path-closest-point.ts';

export { type PathStyleForLegacyCaps } from './types.ts';
